import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { UserProfile } from '@/core/types/models/UserProfile';
import type { components } from '@/core/types/generated/api';
import { AuthService } from '@/shared/services/auth';

type UserDto = components["schemas"]["UserDto"];

type AssetGeneralInfoCardDefaultState = 'expanded' | 'collapsed';

type AssetTreeSettings = {
  includeInactive: boolean;
  fontScale: number;
  generalInfoCardDefaultState: AssetGeneralInfoCardDefaultState;
};

const ASSET_TREE_FONT_SCALE_MIN = 0.5;
const ASSET_TREE_FONT_SCALE_MAX = 2;

const DEFAULT_ASSET_TREE_SETTINGS: AssetTreeSettings = {
  includeInactive: false,
  fontScale: 0.85,
  generalInfoCardDefaultState: 'expanded',
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeAssetTreeSettings(
  settings?: Partial<AssetTreeSettings>
): AssetTreeSettings {
  const includeInactive = typeof settings?.includeInactive === 'boolean'
    ? settings.includeInactive
    : DEFAULT_ASSET_TREE_SETTINGS.includeInactive;

  const rawFontScale = Number(settings?.fontScale ?? DEFAULT_ASSET_TREE_SETTINGS.fontScale);
  const isValidFontScale = Number.isFinite(rawFontScale);
  const clampedFontScale = clamp(
    isValidFontScale ? rawFontScale : DEFAULT_ASSET_TREE_SETTINGS.fontScale,
    ASSET_TREE_FONT_SCALE_MIN,
    ASSET_TREE_FONT_SCALE_MAX
  );

  const generalInfoCardDefaultState = settings?.generalInfoCardDefaultState === 'collapsed'
    ? 'collapsed'
    : 'expanded';

  return {
    includeInactive,
    fontScale: Number(clampedFontScale.toFixed(2)),
    generalInfoCardDefaultState,
  };
}

function assetTreeSettingsEqual(
  a: AssetTreeSettings,
  b: AssetTreeSettings
): boolean {
  return (
    a.includeInactive === b.includeInactive &&
    Math.abs(a.fontScale - b.fontScale) < 0.0001 &&
    a.generalInfoCardDefaultState === b.generalInfoCardDefaultState
  );
}

// Default empty profile
const EMPTY_PROFILE: UserProfile = {
  id: '',
  name: '',
  email: '',
  designation: undefined
};

export const useProfileStore = defineStore('profile', () => {
  const profile = ref<UserProfile>({ ...EMPTY_PROFILE });
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Ensure loading state is reset on store initialization
  // This prevents persisted loading states from previous sessions
  loading.value = false;

  // Theme state: 'light', 'dark', or 'system'
  const theme = ref<'light' | 'dark' | 'system'>('system');

  // Asset tree UI preferences
  const assetTreeSettings = ref<AssetTreeSettings>(normalizeAssetTreeSettings());

  watch(
    assetTreeSettings,
    (newValue) => {
      const normalized = normalizeAssetTreeSettings(newValue);
      if (!assetTreeSettingsEqual(newValue, normalized)) {
        assetTreeSettings.value = normalized;
      }
    },
    { deep: true, immediate: true }
  );

  const isUsingDefaultAssetTreeSettings = computed(() =>
    assetTreeSettingsEqual(assetTreeSettings.value, DEFAULT_ASSET_TREE_SETTINGS)
  );

  function setAssetTreeSettings(update: Partial<AssetTreeSettings>) {
    assetTreeSettings.value = normalizeAssetTreeSettings({
      ...assetTreeSettings.value,
      ...update,
    });
  }

  function resetAssetTreeSettings() {
    assetTreeSettings.value = normalizeAssetTreeSettings(DEFAULT_ASSET_TREE_SETTINGS);
  }

  /**
   * Maps UserDto from API to UserProfile for the store
   */
  function mapUserDtoToProfile(userDto: UserDto): UserProfile {
    return {
      id: userDto.id || '',
      name: userDto.fullName || '',
      email: userDto.email || '',
      designation: userDto.designation || undefined
    };
  }

  /**
   * Loads user profile from the API
   */
  async function loadProfile(includeProjectMemberships = false): Promise<boolean> {
    loading.value = true;
    error.value = null;
    
    try {
      const userDto = await AuthService.getCurrentUser(includeProjectMemberships);
      if (userDto) {
        profile.value = mapUserDtoToProfile(userDto);
        return true;
      } else {
        error.value = 'Failed to load user profile';
        return false;
      }
    } catch (e: any) {
      error.value = e?.message || 'Failed to load user profile';
      return false;
    } finally {
      loading.value = false;
    }
  }

  function setProfile(newProfile: UserProfile) {
    profile.value = newProfile;
    error.value = null; // Clear any previous errors
  }

  /**
   * Updates the user profile both locally and on the backend
   */
  async function updateProfile(updatedProfile: UserProfile): Promise<boolean> {
    loading.value = true;
    error.value = null;
    
    try {
      // Prepare the data for the API call
      const profileData = {
        fullName: updatedProfile.name,
        email: updatedProfile.email,
        designation: updatedProfile.designation
      };
      
      const updatedUserDto = await AuthService.updateCurrentUserProfile(profileData);
      if (updatedUserDto) {
        // Update local state with the response from the server
        profile.value = mapUserDtoToProfile(updatedUserDto);
        return true;
      } else {
        error.value = 'Failed to update user profile';
        return false;
      }
    } catch (e: any) {
      error.value = e?.message || 'Failed to update user profile';
      return false;
    } finally {
      loading.value = false;
    }
  }

  function clearProfile() {
    profile.value = { ...EMPTY_PROFILE };
    error.value = null;
  }

  function setTheme(newTheme: 'light' | 'dark' | 'system') {
    theme.value = newTheme;
  }

  // ...getUserRole moved to composable useUserRole.ts...

  return {
    profile,
    loading,
    error,
    setProfile,
    updateProfile,
    clearProfile,
    loadProfile,
    theme,
    setTheme,
    assetTreeSettings,
    setAssetTreeSettings,
    resetAssetTreeSettings,
    isUsingDefaultAssetTreeSettings,
  };
}, {
  persist: {
    // Exclude loading and error states from persistence
    // This prevents stale loading/error states from previous sessions
    omit: ['loading', 'error']
  },
});
